"use client";

import { Box, Typography } from "@mui/material";
import { formatCurrency } from "@configs/currencies";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { useOrderCredit } from "src/hooks/useOrderCredit";
import { creditCurrencyOf, creditProgressMessage, creditProgressView } from "src/utils/credit-reminder";

/**
 * "Dodajte še za 12,00 € in porabite 10,00 € dobroimetja", with a bar of the
 * goods against the minimum, on the upload page while the order is built.
 *
 * Only for credit WITH a minimum: the figures are the server's
 * (`lockedByMinimum` / `orderGoodsCents` from GET /credit/balance with this
 * order's id, through `useOrderCredit`, which only reads here), never
 * re-derived. Once the order reaches the minimum it says the credit will
 * come off at checkout — it does that by itself, so there is nothing to tap.
 *
 * Nothing at all without credit with a minimum, before the order exists, or
 * for a guest whose claim token cannot be matched to this order yet (no
 * email on it until the delivery details): `useOrderCredit` does not ask
 * without a credential it may send.
 */
export default function CreditMinimumProgress() {
  const t = useAppTranslate();
  const { minimumLines, lots, currency } = useOrderCredit();
  const view = creditProgressView({ lines: minimumLines, lots });
  if (view.kind === "hidden" || !currency) return null;

  const money = (cents: number) => formatCurrency(cents / 100, creditCurrencyOf(currency));
  const message = creditProgressMessage(view, money);
  const text = t(message.key, message.fallback, message.params);

  if (view.kind === "reached") {
    return (
      <Box
        role="status"
        data-testid="credit-minimum-progress"
        sx={(theme) => ({
          mt: 2,
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: "10px",
          bgcolor: theme.palette.mode === "dark" ? "rgba(22, 101, 52, 0.25)" : "#EAF7EE",
          color: theme.palette.mode === "dark" ? "#86EFAC" : "#166534",
          display: "flex",
          alignItems: "center",
          gap: 1,
        })}
      >
        <Box
          component="svg"
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{ width: 18, height: 18, flexShrink: 0 }}
        >
          <path d="M20 6L9 17l-5-5" />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "inherit" }}>{text}</Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="credit-minimum-progress"
      sx={(theme) => ({
        mt: 2,
        mb: 2,
        px: 2,
        py: 1.75,
        borderRadius: "12px",
        border: "1.5px solid #58B4CA",
        bgcolor: theme.palette.mode === "dark" ? "rgba(88, 180, 202, 0.15)" : "background.paper",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      })}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>{text}</Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${money(view.goodsCents)} / ${money(view.minimumOrderCents)}`}
        </Typography>
      </Box>
      <Box
        role="progressbar"
        aria-label={text}
        aria-valuemin={0}
        aria-valuemax={view.minimumOrderCents}
        aria-valuenow={view.goodsCents}
        aria-valuetext={`${money(view.goodsCents)} / ${money(view.minimumOrderCents)}`}
        sx={(theme) => ({
          height: 10,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: theme.palette.mode === "dark" ? "rgba(88, 180, 202, 0.25)" : "#E3EEF1",
        })}
      >
        <Box
          sx={{
            width: `${view.percent}%`,
            height: "100%",
            borderRadius: 999,
            bgcolor: "#58B4CA",
            transition: "width 300ms ease",
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.4 }}>
        {t("creditReminder.progressShipping", "Poštnina se ne šteje.")}
      </Typography>
    </Box>
  );
}
